import { useState } from "react";
import { useDraft } from "../../hooks/useDraft";

const NO_CHANGES = {};

// Clears a field's error as soon as it changes
const clearing = (setErrors, name) => setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));

// A create form kept as a draft until `reset` (on Cancel or after a successful create)
export const useDraftForm = (key, empty) => {
  const [form, setForm, clearForm] = useDraft(key, empty);
  const [errors, setErrors] = useState({});

  const update = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    clearing(setErrors, name);
  };

  const reset = () => {
    clearForm();
    setErrors({});
  };

  return { form, setForm, errors, setErrors, update, reset };
};

/**
 * Edits to a saved record's form, kept as a draft of only the changed fields (never the loaded
 * data). `extra` adds fields that are not saved values, such as a reason; they are never "changes".
 */
export const useFieldChanges = (key, original, extra) => {
  const [changes, setChanges, clearChanges] = useDraft(key, NO_CHANGES);
  const [errors, setErrors] = useState({});
  const values = { ...original, ...extra, ...changes };
  const changedFields = Object.keys(original).filter((name) => name in changes && changes[name] !== original[name]);

  const update = (e) => {
    const { name, value } = e.target;
    setChanges((prev) => {
      const next = { ...prev, [name]: value };
      // Back to the saved value: no longer a change
      if (name in original && value === original[name]) delete next[name];
      return next;
    });
    clearing(setErrors, name);
  };

  const discard = () => {
    clearChanges();
    setErrors({});
  };

  return { values, changedFields, errors, setErrors, update, discard };
};
